/**
 * SeasonalPricingSection
 *
 * SCRUM-1282: Guide Dashboard — Seasonal Pricing UI
 * Full CRUD for season configs. Month/day picker (year-agnostic),
 * wrap-around support, dual validation (inline + 400 handling).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Plus, Pencil, Trash2, Loader2, AlertCircle, X } from 'lucide-react';
import { getSeasons, getSeasonTypes, addSeason, updateSeason, deleteSeason } from '../../../services/guidePricingApi';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

function formatMonthDay(dateStr) {
  if (!dateStr) { return ''; }
  // Handle both "2000-06-01" and "2000-06-01T00:00:00.000Z" (ISO timestamp from API)
  const dateOnly = String(dateStr).slice(0, 10);
  const d = new Date(dateOnly + 'T00:00:00');
  const month = d.getMonth();
  const day = d.getDate();
  if (isNaN(month) || isNaN(day)) { return ''; }
  return `${MONTH_NAMES[month]} ${day}`;
}

function isWrapAround(startDate, endDate) {
  if (!startDate || !endDate) { return false; }
  return startDate > endDate;
}

function toDateString(month, day) {
  const m = String(month).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `2000-${m}-${d}`;
}

const SEASON_LABELS = {
  peak: { label: 'Peak Season', color: 'bg-red-100 text-red-800' },
  shoulder: { label: 'Shoulder Season', color: 'bg-yellow-100 text-yellow-800' },
  off_season: { label: 'Off Season', color: 'bg-blue-100 text-blue-800' }
};

export default function SeasonalPricingSection({ experienceId }) {
  const [seasons, setSeasons] = useState({});
  const [seasonTypes, setSeasonTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [form, setForm] = useState({
    seasonTypeId: '',
    startMonth: '',
    startDay: '',
    endMonth: '',
    endDay: '',
    label: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [seasonsRes, typesRes] = await Promise.all([
        getSeasons(experienceId),
        getSeasonTypes(experienceId)
      ]);
      setSeasons(seasonsRes.data || {});
      setSeasonTypes(typesRes.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load seasonal pricing');
    } finally {
      setLoading(false);
    }
  }, [experienceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const allSeasons = Object.values(seasons).flat();

  const resetForm = () => {
    setForm({ seasonTypeId: '', startMonth: '', startDay: '', endMonth: '', endDay: '', label: '' });
    setFormError(null);
    setEditingSeason(null);
    setShowForm(false);
  };

  const openAddForm = () => {
    resetForm();
    if (seasonTypes.length > 0) {
      setForm(f => ({ ...f, seasonTypeId: seasonTypes[0].id }));
    }
    setShowForm(true);
  };

  const openEditForm = (season) => {
    const start = new Date(season.start_date + 'T00:00:00');
    const end = new Date(season.end_date + 'T00:00:00');
    setForm({
      seasonTypeId: season.season_type_id,
      startMonth: String(start.getMonth() + 1),
      startDay: String(start.getDate()),
      endMonth: String(end.getMonth() + 1),
      endDay: String(end.getDate()),
      label: season.label || ''
    });
    setEditingSeason(season);
    setFormError(null);
    setShowForm(true);
  };

  const validateForm = () => {
    const { startMonth, startDay, endMonth, endDay } = form;
    const sm = parseInt(startMonth, 10);
    const sd = parseInt(startDay, 10);
    const em = parseInt(endMonth, 10);
    const ed = parseInt(endDay, 10);

    if (!sm || sm < 1 || sm > 12) { return 'Start month must be between 1 and 12'; }
    if (!sd || sd < 1 || sd > 31) { return 'Start day must be between 1 and 31'; }
    if (!em || em < 1 || em > 12) { return 'End month must be between 1 and 12'; }
    if (!ed || ed < 1 || ed > 31) { return 'End day must be between 1 and 31'; }
    if (!form.seasonTypeId) { return 'Season type is required'; }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSaving(true);
    setFormError(null);

    const startDate = toDateString(form.startMonth, form.startDay);
    const endDate = toDateString(form.endMonth, form.endDay);

    try {
      if (editingSeason) {
        await updateSeason(experienceId, editingSeason.id, {
          startDate,
          endDate,
          label: form.label || undefined
        });
      } else {
        await addSeason(experienceId, {
          seasonTypeId: form.seasonTypeId,
          startDate,
          endDate,
          label: form.label || undefined
        });
      }
      resetForm();
      await fetchData();
    } catch (err) {
      const apiError = err.error?.message || err.message || 'Failed to save season';
      setFormError(apiError);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (seasonId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this season configuration?')) { return; }
    setDeletingId(seasonId);
    try {
      await deleteSeason(experienceId, seasonId);
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to delete season');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Seasonal Pricing</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="seasonal-pricing-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Seasonal Pricing</h3>
        </div>
        {!showForm && (
          <button
            onClick={openAddForm}
            className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            data-testid="add-season-btn"
          >
            <Plus className="h-4 w-4" />
            Add Season
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg border" data-testid="season-form">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm text-gray-700">
              {editingSeason ? 'Edit Season' : 'Add Season'}
            </h4>
            <button type="button" onClick={resetForm} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Season Type</label>
              <select
                value={form.seasonTypeId}
                onChange={(e) => setForm(f => ({ ...f, seasonTypeId: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm"
                disabled={!!editingSeason}
                data-testid="season-type-select"
              >
                {seasonTypes.map(st => (
                  <option key={st.id} value={st.id}>{st.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label (optional)</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g., Summer Peak"
                className="w-full border rounded-md px-3 py-2 text-sm"
                maxLength={100}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <div className="flex gap-2">
                <select
                  value={form.startMonth}
                  onChange={(e) => setForm(f => ({ ...f, startMonth: e.target.value }))}
                  className="flex-1 border rounded-md px-2 py-2 text-sm"
                  data-testid="start-month"
                >
                  <option value="">Month</option>
                  {MONTH_NAMES.map((name, i) => (
                    <option key={i} value={String(i + 1)}>{name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={form.startDay}
                  onChange={(e) => setForm(f => ({ ...f, startDay: e.target.value }))}
                  placeholder="Day"
                  className="w-20 border rounded-md px-2 py-2 text-sm"
                  data-testid="start-day"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <div className="flex gap-2">
                <select
                  value={form.endMonth}
                  onChange={(e) => setForm(f => ({ ...f, endMonth: e.target.value }))}
                  className="flex-1 border rounded-md px-2 py-2 text-sm"
                  data-testid="end-month"
                >
                  <option value="">Month</option>
                  {MONTH_NAMES.map((name, i) => (
                    <option key={i} value={String(i + 1)}>{name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={form.endDay}
                  onChange={(e) => setForm(f => ({ ...f, endDay: e.target.value }))}
                  placeholder="Day"
                  className="w-20 border rounded-md px-2 py-2 text-sm"
                  data-testid="end-day"
                />
              </div>
            </div>
          </div>

          {form.startMonth && form.endMonth && isWrapAround(
            toDateString(form.startMonth, form.startDay || '1'),
            toDateString(form.endMonth, form.endDay || '1')
          ) && (
            <div className="mb-4 p-2 bg-amber-50 text-amber-700 rounded text-sm">
              This season wraps around the year boundary (e.g., Dec &rarr; Mar).
            </div>
          )}

          {formError && (
            <div className="mb-4 p-2 bg-red-50 text-red-700 rounded text-sm flex items-center gap-2" data-testid="season-form-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {formError}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={resetForm}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-1.5 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
              data-testid="season-save-btn"
            >
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}
              {editingSeason ? 'Update' : 'Add Season'}
            </button>
          </div>
        </form>
      )}

      {allSeasons.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="seasons-empty">
          <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No seasons configured</p>
          <p className="text-xs text-gray-400 mt-1">Add seasons to adjust pricing by time of year</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="seasons-list">
          {allSeasons.map(season => {
            const badge = SEASON_LABELS[season.season_code] || { label: season.season_name, color: 'bg-gray-100 text-gray-800' };
            const wrap = isWrapAround(season.start_date, season.end_date);
            return (
              <div
                key={season.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                data-testid="season-row"
              >
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                    {badge.label}
                  </span>
                  <span className="text-sm text-gray-700">
                    {formatMonthDay(season.start_date)} &ndash; {formatMonthDay(season.end_date)}
                    {wrap && <span className="text-amber-600 ml-1 text-xs">(wraps year)</span>}
                  </span>
                  {season.label && (
                    <span className="text-xs text-gray-400">{season.label}</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditForm(season)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                    title="Edit"
                    data-testid="edit-season-btn"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(season.id)}
                    disabled={deletingId === season.id}
                    className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                    title="Delete"
                    data-testid="delete-season-btn"
                  >
                    {deletingId === season.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Trash2 className="h-4 w-4" />
                    }
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
